import { mergeMap, map, filter, tap, switchMap, catchError } from 'rxjs/operators';
import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, EMPTY, Subscription, timer } from 'rxjs';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Guid } from 'src/utility/guid';
import { AppConfig } from 'src/app/app.config';
import { LogHelper } from '../logging/log-helper';
import { AuthService } from '../authentication/auth.service';
import { AccessControl } from '../authentication/access-control';
import { ResponsiveService, ViewState } from '../responsive.service';

export interface BannerInfo {
  id: string;
  enabled: boolean;
  message: string;
  background: string;
  foreground: string;
  link: string;
  ignored: boolean;
}

export interface NavInfo {
  name?: string;
  url?: string;
}

export class LayoutInfo {
  static readonly largeToolbarHeight = 64;
  static readonly smallToolbarHeight = 56;

  constructor(
    public toolbarHeight: number,
    public bannerHeight: number,
  ) {}

  getCombinedTopOffsetHeight(): number {
    return (this.toolbarHeight || largeToolbarHeight) + (this.bannerHeight || 0);
  }
}

// NOT YET IMPLEMENTED
// const IGNORED_BANNER_LOCAL_STORAGE_KEY = 'ignoredBannerIds';

const largeToolbarHeight = 64;
const smallToolbarHeight = 56;

@Injectable()
export class LayoutService {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthService);
  private readonly responsive = inject(ResponsiveService);

  // These subjects should be considered the Single Source of Truth for the
  // information they provide. Components should avoid storing these values -
  // instead, always get the values from this service.
  navInfo$: BehaviorSubject<NavInfo> = new BehaviorSubject<NavInfo>(null);
  bannerInfo$: BehaviorSubject<BannerInfo> = new BehaviorSubject<BannerInfo>(null);
  // Note: most components should not need to subscribe to this subject, because
  // our root LayoutComponent initiates change detection when updates occur
  layoutInfo$: BehaviorSubject<LayoutInfo> = new BehaviorSubject<LayoutInfo>(this.createLayoutInfo());

  get responsiveViewState(): ViewState {
    return this.responsive.state;
  }
  get isBannerAllowed(): boolean {
    return this.auth.hasPermission(AccessControl.FeatureSystemMessageBanner);
  }
  get isBannerEnabled(): boolean {
    return this.isBannerAllowed && this.bannerInfo$.value?.enabled === true;
  }

  // guaranteed non-null
  get currentLayoutInfo(): LayoutInfo {
    return this.layoutInfo$.value ?? this.createLayoutInfo();
  }
  // guaranteed non-zero, non-null - combined height of top toolbar + banner (if enabled)
  get layoutHeightOffsetTop(): number {
    return this.toolbarHeight + this.bannerHeight;
  }
  // guaranteed non-zero, non-null
  get toolbarHeight(): number {
    return this.currentLayoutInfo.toolbarHeight;
  }
  // guaranteed non-null
  get bannerHeight(): number {
    return this.currentLayoutInfo.bannerHeight;
  }

  // NOT YET IMPLEMENTED
  // private get ignoredBannerIds(): string[] {
  //     return localStorage.getItem(IGNORED_BANNER_LOCAL_STORAGE_KEY)?.split(',') || [];
  // }

  private readonly log = new LogHelper('Layout');

  constructor() {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => this.route),
        map((r) => {
          while (r.firstChild) {
            r = r.firstChild;
          }
          return r;
        }),
        filter((r) => r.outlet === 'primary'),
        mergeMap((r) => r.data),
      )
      .subscribe((event) => {
        const info: NavInfo = {
          name: event['name'],
          url: event['url'],
        };
        this.publishNavUpdate(info);
      });

    let lastViewState = this.responsive.state;
    this.responsive.updates.subscribe((state) => {
      const didCrossBreakpoint = lastViewState.isSmallish !== state.isSmallish;
      lastViewState = state;
      if (didCrossBreakpoint) {
        this.publishLayoutUpdate(this.createLayoutInfo(this.bannerHeight, state.isSmallish));
      }
    });
  }

  // creates a new LayoutInfo model
  private createLayoutInfo(banner: number = 0, isSmall = this.responsiveViewState.isSmallish): LayoutInfo {
    const toolbar = isSmall ? smallToolbarHeight : largeToolbarHeight;
    return new LayoutInfo(toolbar, banner);
  }

  publishLayoutUpdate(info: LayoutInfo) {
    const merged = this.currentLayoutInfo;
    merged.toolbarHeight = info.toolbarHeight;
    merged.bannerHeight = info.bannerHeight;
    this.layoutInfo$.next(merged);
  }

  publishNavUpdate(info: NavInfo) {
    const merged = { ...this.navInfo$.value, ...info };
    this.navInfo$.next(merged);
  }

  private lastBannerConfigPollSubscription: Subscription;
  startPollingBannerConfig(brandName: string) {
    // cancel the existing polling subscription, if present
    this.lastBannerConfigPollSubscription?.unsubscribe();

    // early exit if we don't have configPath or brandName
    const configPath = AppConfig.banner?.configPath;
    if (!configPath || !brandName) return;
    this.log.debug(`start polling for banner config: platform=${brandName}`);

    const checkMinutes = 1; // check for config updates every minute
    this.lastBannerConfigPollSubscription = timer(0, 60000 * checkMinutes)
      .pipe(
        switchMap(() => {
          // don't fetch if disabled via feature toggle
          if (!this.isBannerAllowed) return EMPTY;

          const url = `${configPath}${brandName.toLowerCase()}/config.json`;
          // this prevents us from getting cached responses for the config.json
          const uniqueUrl = `${url}?unique=${Guid.newGuid()}`;
          const headers = new HttpHeaders()
            .set('Accept', 'application/json')
            .set('Pragma', 'no-cache')
            .set('Expires', 'Sat, 01 Jan 2000 00:00:00 GMT');
          return this.http.get<BannerInfo>(uniqueUrl, { headers: headers }).pipe(catchError((_err) => EMPTY));
        }),
        tap((info) => {
          // NOT YET IMPLEMENTED
          // info.ignored = this.ignoredBannerIds.includes(info.id);
          this.bannerInfo$.next(info);
        }),
      )
      .subscribe();
  }

  // NOT YET IMPLEMENTED
  // ignoreCurrentBanner(bannerHeight: number) {
  // 	const bannerConfig = this.bannerConfig$.value
  // 	bannerConfig.height = 0;
  // 	if (!this.ignoredBannerIds.includes(bannerConfig.id)) {
  // 		localStorage.setItem(IGNORED_BANNER_LOCAL_STORAGE_KEY, [bannerConfig.id, ...this.ignoredBannerIds].join(','));
  // 	}
  // 	this.publishBannerConfig(bannerConfig);
  // }

  // NOT YET IMPLEMENTED
  // showIgnoredBanner() {
  // 	const bannerConfig = this.bannerConfig$.value
  // 	localStorage.setItem(IGNORED_BANNER_LOCAL_STORAGE_KEY, this.ignoredBannerIds.filter(x => x != bannerConfig.id).join(','));
  // 	this.publishBannerConfig(bannerConfig);
  // }
}
